/* eslint-disable max-lines */
import { Image, Text, type UseModalState, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogActions, DialogContent, Stack, useTheme } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";
import { type HcfTimeclockComplianceVideoConfig } from "@src/appV2/Shifts/Shift/ShiftState/types";
import { ClockActionPictureDialogEvent } from "@src/appV2/Shifts/Shift/TakeClockActionPicture/ClockActionPictureDialogEvent";
import transparentPixelImage from "public/assets/images/transparent-pixel.png";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

import startRecordingButtonImage from "../assets/start_recording_button.svg";
import stopRecordingButtonImage from "../assets/stop_recording_button.svg";
import { useVideoRecorder } from "../hooks/useVideoRecorder";
import { VideoPreviewPlayer } from "./VideoPreviewPlayer";
import { VideoRecordingErrorBottomSheet } from "./VideoRecordingErrorBottomSheet";
import { VideoRetakeConfirmationBottomSheet } from "./VideoRetakeConfirmationBottomSheet";

interface VideoRecordingDialogProps {
  modalState: UseModalState;
  videoConfig: HcfTimeclockComplianceVideoConfig | undefined;
  logData: Record<string, unknown>;
  isUploading: boolean;
  videoReviewNotice: ReactNode;
  onClose: () => void;
  onRecordVideoSuccess: (blob: Blob) => Promise<void>;
}

export function VideoRecordingDialog(props: VideoRecordingDialogProps) {
  const {
    modalState,
    videoConfig,
    logData,
    isUploading,
    videoReviewNotice,
    onClose,
    onRecordVideoSuccess,
  } = props;

  const theme = useTheme();
  const confirmDeletionModalState = useModalState("closed");
  const recordingErrorModalState = useModalState("closed");

  const [videoPreviewElementRef, setVideoPreviewElementRef] = useState<HTMLVideoElement>();
  const lastReceivedErrorRef = useRef<string>();

  const [recordedVideoUrl, setRecordedVideoUrl] = useState<string>();
  const videoBlobRef = useRef<Blob>();
  const [isRetrying, setIsRetrying] = useState(false);

  const { showErrorToast } = useToast();

  const onVideoRecorded = useCallback((blob: Blob) => {
    videoBlobRef.current = blob;
    // FIXME: Automatically disabled during ESLint config update
    setRecordedVideoUrl(URL.createObjectURL(blob));
  }, []);

  const logTimeclockEvent = useCallback(
    (eventAction: ClockActionPictureDialogEvent, meta?: Record<string, any>) => {
      logEvent(APP_V2_USER_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_EVENT, {
        ...logData,
        ...meta,
        eventAction,
      });
    },
    [logData]
  );

  const {
    startRecording,
    stopRecording,
    initialise,
    destroy,
    isReady,
    isRecording,
    recordingTime,
    isErrored: isVideoErrored,
    errorMessage: videoInitErrorMessage,
  } = useVideoRecorder({
    videoPreviewRef: videoPreviewElementRef,
    onVideoRecorded,
    maxRecordingTime: videoConfig?.maxVideoLength ?? 60,
    resolution: {
      width: videoConfig?.videoWidth ?? 414,
      height: videoConfig?.videoHeight ?? 736,
    },
    videoBitsPerSecond: videoConfig?.videoBitRate,
  });

  useEffect(() => {
    if (!videoInitErrorMessage || videoInitErrorMessage === lastReceivedErrorRef.current) {
      return;
    }

    lastReceivedErrorRef.current = videoInitErrorMessage;
    logError(APP_V2_APP_EVENTS.FACILITY_TIMECLOCK_COMPLIANCE_ERROR, {
      error: new Error(videoInitErrorMessage),
      metadata: { logData, location: "init-or-start-recording" },
    });
    logTimeclockEvent(ClockActionPictureDialogEvent.RECORDING_FAILURE, {
      errorMessage: videoInitErrorMessage,
      recordingTime,
      isRecording,
    });
  }, [videoInitErrorMessage, isRecording, logData, recordingTime, logTimeclockEvent]);

  useEffect(() => {
    if (isVideoErrored && videoInitErrorMessage) {
      recordingErrorModalState.openModal();
    }
  }, [isVideoErrored, videoInitErrorMessage, recordingErrorModalState]);

  // Initialize camera when component mounts
  useEffect(() => {
    if (videoPreviewElementRef) {
      // eslint-disable-next-line promise/prefer-await-to-then
      initialise().catch(() => {
        // Error is handled by hook state (isErrored/errorMessage)
        // which will trigger the error modal
      });
    }
  }, [videoPreviewElementRef, initialise]);

  const onRetryRecording = async () => {
    setIsRetrying(true);
    try {
      await initialise();
      recordingErrorModalState.closeModal();
    } catch {
      // Error already handled by hook state (isErrored/errorMessage)
      // Bottom sheet stays open to show error
    } finally {
      setIsRetrying(false);
    }
  };

  const onGoBackFromError = () => {
    setIsRetrying(false);
    recordingErrorModalState.closeModal();
    onClose();
  };

  return (
    <>
      {recordedVideoUrl ? (
        <FullScreenDialog modalState={modalState} variant="primary">
          <PageHeaderTopRow
            title="Looks good?"
            sx={(theme) => ({
              "& .MuiTypography-root": {
                ...theme.typography.h4,
                fontWeight: theme.typography.fontWeightMedium,
                textAlign: "center",
                width: "100%",
              },
            })}
            onClickBack={onClose}
          />
          <DialogContent sx={{ px: 7, pt: 0 }}>
            <Stack spacing={4} alignItems="stretch">
              {videoReviewNotice}
              <Box>
                <VideoPreviewPlayer recordedVideoUrl={recordedVideoUrl} />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              px: 7,
              pt: 4,
              ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(4) }),
            }}
          >
            <Stack spacing={3} direction="column" width="100%">
              <LoadingButton
                fullWidth
                variant="contained"
                size="large"
                isLoading={isUploading}
                onClick={async () => {
                  if (isUploading) {
                    return;
                  }

                  await onRecordVideoSuccess(videoBlobRef.current!);
                }}
              >
                Looks great! Submit video
              </LoadingButton>
              <Button
                fullWidth
                variant="text"
                disabled={isUploading}
                onClick={() => {
                  confirmDeletionModalState.openModal();
                }}
              >
                Try again
              </Button>
            </Stack>
          </DialogActions>
        </FullScreenDialog>
      ) : (
        <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="primary">
          <DialogContent sx={{ display: "flex", position: "relative", padding: 0 }}>
            <video
              ref={(ref) => {
                setVideoPreviewElementRef(ref ?? undefined);
              }}
              playsInline
              autoPlay
              muted
              style={{
                background: "black",
                objectFit: "contain",
                width: "100%",
                height: "100%",
              }}
              // To prevent android from showing askewed play icon until video loads
              poster={transparentPixelImage}
            />

            <Stack
              spacing={2}
              sx={{
                position: "absolute",
                bottom: "3vh",
                left: 0,
                width: "100%",
                alignItems: "center",
              }}
            >
              <Text color="white">
                {isDefined(recordingTime)
                  ? `0:${recordingTime.toString().padStart(2, "0")}`
                  : "-:--"}
              </Text>

              <Box
                component="button"
                sx={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  "&:hover": { transform: "scale(1.05)" },
                }}
                title={isRecording ? "Stop recording" : "Start recording"}
                onClick={async () => {
                  if (!isReady || recordedVideoUrl) {
                    return;
                  }

                  if (isRecording) {
                    if (
                      isDefined(videoConfig?.minVideoLength) &&
                      isDefined(recordingTime) &&
                      recordingTime < videoConfig.minVideoLength
                    ) {
                      showErrorToast(`Video must be at least ${videoConfig.minVideoLength}s long.`);
                      return;
                    }

                    await stopRecording();
                    logTimeclockEvent(ClockActionPictureDialogEvent.RECORDING_DONE, {
                      recordingTime,
                    });
                    return;
                  }

                  await startRecording();
                  logTimeclockEvent(ClockActionPictureDialogEvent.RECORDING_START);
                }}
              >
                <Image
                  src={isRecording ? stopRecordingButtonImage : startRecordingButtonImage}
                  alt={isRecording ? "Stop recording" : "Start recording"}
                  width="8vh"
                  height="8vh"
                />
              </Box>

              <Button
                variant="text"
                size="large"
                sx={{
                  // White text styling for video background visibility
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
                onClick={() => {
                  logTimeclockEvent(ClockActionPictureDialogEvent.RECORDING_CANCEL, {
                    recordingTime,
                    isRecording,
                  });
                  onClose();
                }}
              >
                Cancel
              </Button>
            </Stack>
          </DialogContent>
        </FullScreenDialog>
      )}
      <VideoRetakeConfirmationBottomSheet
        modalState={confirmDeletionModalState}
        onContinue={() => {
          setRecordedVideoUrl(undefined);
          confirmDeletionModalState.closeModal();
          // Reinitialize camera for fresh recording
          destroy();
          // eslint-disable-next-line promise/prefer-await-to-then
          initialise().catch(() => {
            // Error is handled by hook state (isErrored/errorMessage)
          });
        }}
        onCancel={() => {
          confirmDeletionModalState.closeModal();
        }}
      />
      <VideoRecordingErrorBottomSheet
        modalState={recordingErrorModalState}
        errorText={videoInitErrorMessage}
        isLoading={isRetrying}
        onRetry={onRetryRecording}
        onGoBack={onGoBackFromError}
      />
    </>
  );
}
/* eslint-enable max-lines */
